import { Button, ButtonVariant, Modal } from "@swivl-mono/ui";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { StepApplyOutcome } from "./workflow";

/** A step's apply state. Held by the tab, not the bar: a tab swaps itself for a loading spinner while
 * it re-fetches after applying, which unmounts the bar, and the outcome must survive that. */
export interface StepApplyState {
  applying: boolean;
  outcome: StepApplyOutcome | null;
  error: string | null;
  run: () => Promise<void>;
}

export function useStepApply(onApply: () => Promise<StepApplyOutcome>): StepApplyState {
  const [applying, setApplying] = useState(false);
  const [outcome, setOutcome] = useState<StepApplyOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (): Promise<void> => {
    setApplying(true);
    setError(null);
    try {
      setOutcome(await onApply());
    } catch (err) {
      setOutcome(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setApplying(false);
    }
  }, [onApply]);

  return { applying, outcome, error, run };
}

interface StepApplyBarProps {
  /** What Apply will do right now, e.g. "12 value(s) will migrate". */
  description: string;
  /** Button and modal title, e.g. "Apply Details". */
  applyLabel: string;
  /** The confirm modal's body: what this writes, in plain words. */
  confirmText: string;
  disabled?: boolean;
  /** Shown instead of `description` while disabled, so the operator knows what they are waiting for. */
  disabledReason?: string;
  state: StepApplyState;
}

/**
 * One step's Apply control: the button, a confirm modal, and the outcome of the last apply. Each step
 * writes on its own, so this replaces the single Accept-all step; a step can be re-applied any time.
 */
export function StepApplyBar({ description, applyLabel, confirmText, disabled, disabledReason, state }: StepApplyBarProps): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { applying, outcome, error, run } = state;

  const handleOpenConfirm = useCallback(() => setConfirmOpen(true), []);
  const handleCloseConfirm = useCallback(() => setConfirmOpen(false), []);
  const handleConfirm = useCallback(() => {
    setConfirmOpen(false);
    void run();
  }, [run]);

  return (
    <div className="mx-auto max-w-5xl px-6 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-grey-200 bg-grey-50 p-3">
        <span className="body-sm text-grey-600">{disabled && disabledReason ? disabledReason : description}</span>
        <Button variant={ButtonVariant.PRIMARY} onClick={handleOpenConfirm} disabled={disabled || applying}>
          {applying ? "Applying…" : applyLabel}
        </Button>
      </div>
      {outcome && (
        <p className="body-sm mt-2 text-teal-700">
          Applied: {outcome.message}
          {outcome.ticketNumber != null && (
            <>
              {" · ticket "}
              <Link to={`/ticket/${outcome.ticketNumber}`} className="text-orange-500 underline">
                #{outcome.ticketNumber}
              </Link>
            </>
          )}
        </p>
      )}
      {outcome?.warning && <p className="body-xs mt-1 text-yellow-700">{outcome.warning}</p>}
      {error && <p className="body-sm mt-2 text-red-500">Apply failed: {error}</p>}

      <Modal isOpen={confirmOpen} onClose={setConfirmOpen} title={applyLabel} hasTitleBorder>
        <div className="p-4">
          <p className="body-sm text-grey-600">{confirmText}</p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant={ButtonVariant.GHOST} onClick={handleCloseConfirm}>
              Cancel
            </Button>
            <Button variant={ButtonVariant.PRIMARY} onClick={handleConfirm}>
              {applyLabel}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
